import { create } from "zustand";
import { updateCurrentStep } from "../api/client";

export type Step = {
  id: string;
  order?: number;
  name: Record<string, string>;
  description: Record<string, string>;
  chat_placeholder: Record<string, string>;
  skills: string[];
};

const getInitialSessionId = () => {
  if (typeof window === "undefined") return undefined;
  return window.sessionStorage.getItem("sessionId") ?? undefined;
};

const getInitialProjectName = () => {
  if (typeof window === "undefined") return undefined;
  return window.sessionStorage.getItem("projectName") ?? undefined;
};

const getInitialLastProject = () => {
  if (typeof window === "undefined") return undefined;
  return window.sessionStorage.getItem("lastProjectName") ?? undefined;
};

const getInitialAuth = () => {
  if (typeof window === "undefined") return false;
  return window.sessionStorage.getItem("isAuthenticated") === "true";
};

const getInitialUserEmail = () => {
  if (typeof window === "undefined") return undefined;
  return window.sessionStorage.getItem("userEmail") ?? undefined;
};

const getInitialProgress = () => {
  const fallback = {
    audioReady: false,
    transcriptionsReviewed: false,
    scenariosReady: false,
    scenarioChosen: false,
    scenarioEdited: false
  };
  if (typeof window === "undefined") return fallback;
  try {
    const raw = window.sessionStorage.getItem("progress");
    if (raw) return JSON.parse(raw);
  } catch (e) {
    // Ignore
  }
  return fallback;
};

const getInitialMaxReachedStepIndex = () => {
  if (typeof window === "undefined") return 0;
  const raw = window.sessionStorage.getItem("maxReachedStepIndex");
  return raw ? parseInt(raw, 10) : 0;
};

type SessionState = {
  sessionId?: string;
  projectName?: string;
  lastProjectName?: string;
  currentStep?: string;
  language: "fr" | "en";
  steps: Step[];
  chatPlaceholder?: string;
  scenarioTarget: number;
  progress: {
    audioReady: boolean;
    transcriptionsReviewed: boolean;
    scenariosReady: boolean;
    scenarioChosen: boolean;
    scenarioEdited: boolean;
  };
  isAuthenticated: boolean;
  userEmail?: string;
  login: (email: string) => void;
  logout: () => void;
  setProgress: (progress: SessionState["progress"]) => void;
  resetProgress: () => void;
  setLanguage: (lang: "fr" | "en") => void;
  setSteps: (steps: Step[]) => void;
  setCurrentStep: (stepId: string) => void;
  setChatPlaceholder: (placeholder: string) => void;
  setSessionId: (sessionId: string | undefined) => void;
  setProjectName: (name: string | undefined) => void;
  setLastProjectName: (name: string | undefined) => void;
  setScenarioTarget: (count: number) => void;
  maxReachedStepIndex: number;
  setMaxReachedStepIndex: (index: number) => void;
  updateProgress: (patch: Partial<SessionState["progress"]>) => void;
};

export const useSessionStore = create<SessionState>((set) => ({
  language: "fr",
  steps: [],
  scenarioTarget: 3,
  sessionId: getInitialSessionId(),
  projectName: getInitialProjectName(),
  lastProjectName: getInitialLastProject(),
  progress: getInitialProgress(),
  maxReachedStepIndex: getInitialMaxReachedStepIndex(),
  isAuthenticated: getInitialAuth(),
  userEmail: getInitialUserEmail(),
  login: (email) => {
    if (typeof window !== "undefined") {
      window.sessionStorage.setItem("isAuthenticated", "true");
      window.sessionStorage.setItem("userEmail", email);
    }
    set({ isAuthenticated: true, userEmail: email });
  },
  logout: () => {
    if (typeof window !== "undefined") {
      window.sessionStorage.removeItem("isAuthenticated");
      window.sessionStorage.removeItem("userEmail");
      window.sessionStorage.removeItem("projectName");
      window.sessionStorage.removeItem("sessionId");
      window.sessionStorage.removeItem("progress");
      window.sessionStorage.removeItem("maxReachedStepIndex");
    }
    set({
      isAuthenticated: false,
      userEmail: undefined,
      projectName: undefined,
      sessionId: undefined,
      maxReachedStepIndex: 0,
      progress: {
        audioReady: false,
        transcriptionsReviewed: false,
        scenariosReady: false,
        scenarioChosen: false,
        scenarioEdited: false
      }
    });
  },
  setProgress: (progress) => {
    if (typeof window !== "undefined") {
      window.sessionStorage.setItem("progress", JSON.stringify(progress));
    }
    set({
      progress: {
        audioReady: progress.audioReady,
        transcriptionsReviewed: progress.transcriptionsReviewed,
        scenariosReady: progress.scenariosReady,
        scenarioChosen: progress.scenarioChosen,
        scenarioEdited: progress.scenarioEdited
      }
    });
  },
  resetProgress: () => {
    const fresh = {
      audioReady: false,
      transcriptionsReviewed: false,
      scenariosReady: false,
      scenarioChosen: false,
      scenarioEdited: false
    };
    if (typeof window !== "undefined") {
      window.sessionStorage.setItem("progress", JSON.stringify(fresh));
      window.sessionStorage.setItem("maxReachedStepIndex", "0");
    }
    set({ progress: fresh, maxReachedStepIndex: 0 });
  },
  setMaxReachedStepIndex: (index) => {
    if (typeof window !== "undefined") {
      window.sessionStorage.setItem("maxReachedStepIndex", index.toString());
    }
    set({ maxReachedStepIndex: index });
  },
  setLanguage: (language) =>
    set((state) => {
      const step = state.steps.find((s) => s.id === state.currentStep);
      const chatPlaceholder = step?.chat_placeholder?.[language] ?? state.chatPlaceholder;
      return { language, chatPlaceholder };
    }),
  setSteps: (steps) =>
    set((state) => {
      const first = steps[0];
      const match = typeof window !== "undefined" ? window.location.pathname.match(/^\/step\/([^/]+)$/) : null;
      const stepFromUrl = match ? match[1] : undefined;
      const finalStep = stepFromUrl ?? state.currentStep ?? first?.id;
      const stepObj = steps.find((s) => s.id === finalStep) ?? first;
      if (state.sessionId && finalStep) {
        updateCurrentStep(state.sessionId, finalStep).catch((err) => {
          console.error("Failed to sync step from setSteps to backend:", err);
        });
      }
      return {
        steps,
        currentStep: finalStep,
        chatPlaceholder: stepObj?.chat_placeholder?.[state.language] ?? state.chatPlaceholder
      };
    }),
  setCurrentStep: (currentStep) =>
    set((state) => {
      const step = state.steps.find((s) => s.id === currentStep);
      const chatPlaceholder = step?.chat_placeholder?.[state.language] ?? state.chatPlaceholder;
      if (state.sessionId && currentStep) {
        updateCurrentStep(state.sessionId, currentStep).catch((err) => {
          console.error("Failed to sync step to backend:", err);
        });
      }
      return { currentStep, chatPlaceholder };
    }),
  setChatPlaceholder: (chatPlaceholder) => set({ chatPlaceholder }),
  setSessionId: (sessionId) => {
    if (typeof window !== "undefined") {
      if (sessionId) {
        window.sessionStorage.setItem("sessionId", sessionId);
      } else {
        window.sessionStorage.removeItem("sessionId");
      }
    }
    set({ sessionId });
  },
  setProjectName: (projectName) => {
    if (typeof window !== "undefined") {
      if (projectName) {
        window.sessionStorage.setItem("projectName", projectName);
        window.sessionStorage.setItem("lastProjectName", projectName);
      } else {
        window.sessionStorage.removeItem("projectName");
      }
    }
    set({ projectName, lastProjectName: projectName });
  },
  setScenarioTarget: (scenarioTarget) => set({ scenarioTarget }),
  updateProgress: (patch) =>
    set((state) => {
      const next = { ...state.progress, ...patch };
      if (typeof window !== "undefined") {
        window.sessionStorage.setItem("progress", JSON.stringify(next));
      }
      return { progress: next };
    }),
  setLastProjectName: (lastProjectName) => {
    if (typeof window !== "undefined") {
      if (lastProjectName) {
        window.sessionStorage.setItem("lastProjectName", lastProjectName);
      } else {
        window.sessionStorage.removeItem("lastProjectName");
      }
    }
    set({ lastProjectName });
  }
}));
